import { useAppRouter as useRouter } from '@/shared/hooks/useAppRouter'

import { getConfig } from '@/config'
import { useRecord } from '@/shared/hooks'
import { useTenantIdentitySync } from '@/shared/hooks/useTenantIdentitySync'
import type { Config } from '@/types/config'
import type { DigitalProduct } from '@/types/digitalProduct'

type UseDigitalProductProps = {
  shouldFetch?: boolean
  isPublic?: boolean
}

type UseDigitalProductReturn = {
  digitalProductId: string
  digitalProduct: DigitalProduct
  basePathWithId: string
  config: Config
  /**
   * Repassados pelo spread de `useRecord`. Não constavam no tipo, embora o
   * `Guard` já os consumisse — o `as` na chamada descartava o resto do retorno.
   */
  isSuccess?: boolean
  errorStatusCode?: number
  failureCount?: number
  /** Sempre fornecido pelo `useRecord`; o `DigitalProductProvider` o exige. */
  refetch: () => void
}

export function useDigitalProduct(
  props: UseDigitalProductProps = { shouldFetch: true, isPublic: true }
): UseDigitalProductReturn {
  const { shouldFetch = true, isPublic = true } = props

  const router = useRouter()
  let { digitalProductId } = router.query as { digitalProductId?: string }
  const config = getConfig({
    id: typeof digitalProductId === 'string' ? digitalProductId : ''
  })

  const { record: digitalProduct, ...rest } = useRecord({
    r: '/public/digital_products',
    id: digitalProductId,
    shouldFetch,
    options: {
      retry: 10,
      // O poll fixo que existia aqui custava duas requisições por minuto por
      // participante, disputando a rede do evento, para um dado que muda por
      // semana. Quem dependia dele era a contagem regressiva, que já dispara
      // `refetch` pelo `onComplete` do `CountdownPage`.
      refetchOnReconnect: true,
      staleTime: 5 * 60 * 1000
    }
  }) as {
    record: DigitalProduct
    isSuccess?: boolean
    errorStatusCode?: number
    failureCount?: number
    refetch: () => void
  }

  // O retrato de aparência só é atualizado a partir da resposta confirmada.
  // Gravar aqui é o que faz o PRÓXIMO boot abrir certo.
  useTenantIdentitySync(digitalProduct)

  const basePathWithId = digitalProductId ? '' : config.basePathWithId

  return {
    digitalProductId: digitalProductId ?? '',
    digitalProduct,
    basePathWithId,
    config,
    ...rest
  }
}
